import { memo, useMemo, useState, useEffect, useRef } from "react";
import { FiRefreshCcw, FiTrash2 } from "react-icons/fi";
import { cn } from "../../../../lib/cn";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { Button, Spinner, MobileDrawer, showToast } from "../../../../components/shared";
import { CustomPopover } from "../../../../components/shared/Popover";
import {
  ProjectsIcon,
  PlusIcon,
  CheckIcon,
} from "../../../../assets/icons";
import { NO_PROJECT_LABEL } from "../constants";
import { SessionList } from "./SessionList";
import { useClearSummary, useRegenerateSummary } from "../../../../lib/react-query/aiChat";

export const DraftFlowNavigator = memo(
  ({
    projectOptions,
    selectedProjectId,
    projectsLoading,
    onProjectSelect,
    sessions,
    activeSession,
    onSessionSelect,
    onCreateSession,
    creatingSession,
    summaryMeta,
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const searchInputRef = useRef(null);
    const isMobile = useIsMobile();
    const { mutateAsync: regenerate, isPending: regenerating } = useRegenerateSummary();
    const { mutateAsync: clear, isPending: clearing } = useClearSummary();

    const runSummary = async (action) => {
      if (!activeSession?.id) return;
      await action(activeSession.id);
      showToast("Summary updated", "success", { position: "top-center" });
    };

    const selectedProjectName = useMemo(
      () =>
        projectOptions.find((p) => String(p?.id) === String(selectedProjectId))
          ?.name,
      [projectOptions, selectedProjectId],
    );

    const filteredSessions = useMemo(() => {
      if (!selectedProjectId) return sessions;
      return sessions.filter(
        (session) => String(session?.project_id) === String(selectedProjectId),
      );
    }, [sessions, selectedProjectId]);

    const filteredProjects = useMemo(() => {
      const query = search.trim().toLowerCase();
      if (!query) return projectOptions;
      return projectOptions.filter((p) =>
        p?.name?.toLowerCase().includes(query),
      );
    }, [projectOptions, search]);

    useEffect(() => {
      if (isOpen) searchInputRef.current?.focus({ preventScroll: true });
    }, [isOpen]);

    const triggerLabel = selectedProjectName
      ? activeSession?.title
        ? `${selectedProjectName} › ${activeSession.title}`
        : selectedProjectName
      : activeSession?.title
        ? `${NO_PROJECT_LABEL} › ${activeSession.title}`
        : NO_PROJECT_LABEL;

    const handleProjectSelect = (projectId) => {
      onProjectSelect(projectId);
    };

    const handleSessionSelect = (id) => {
      onSessionSelect(id);
      setIsOpen(false);
    };

    const handleCreateSession = () => {
      onCreateSession();
      setIsOpen(false);
    };

    const handleClose = () => {
      setIsOpen(false);
      setSearch("");
    };

    const triggerButton = (
      <button
        type="button"
        aria-label="Select project and session"
        onClick={() => isMobile && setIsOpen(true)}
        className={cn(
          "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors cursor-pointer max-w-[400px]",
          isOpen
            ? "border-primary/30 bg-primary/5 text-primary"
            : "border-surface-border bg-white text-grey hover:border-surface-border hover:bg-surface",
        )}
      >
        <ProjectsIcon
          width={15}
          height={15}
          className="shrink-0 text-mediumgrey"
        />
        <span className="truncate" title={triggerLabel}>{triggerLabel}</span>
        {filteredSessions.length > 0 && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-primary/10 text-[10px] font-semibold text-primary/70 px-1">
            {filteredSessions.length}
          </span>
        )}
        <svg
          width={10}
          height={10}
          viewBox="0 0 10 10"
          className={cn(
            "shrink-0 text-mediumgrey transition-transform",
            isOpen && "rotate-180",
          )}
        >
          <path
            d="M2 3.5L5 6.5L8 3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>
    );

    const drawerContent = (
      <div className="flex flex-col h-full">
        <div className="p-2 border-b border-surface-border bg-surface/50">
          <span className="px-1 text-[10px] font-semibold text-mediumgrey uppercase tracking-wider">
            Project
          </span>
          <input
            ref={searchInputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects..."
            className="w-full mt-1.5 rounded-lg border border-surface-border bg-white px-3 py-1.5 text-xs text-grey placeholder:text-mediumgrey focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
            disabled={projectsLoading}
          />
        </div>
        <div className="max-h-[160px] overflow-y-auto p-1">
          {projectsLoading ? (
            <div className="py-4 flex items-center justify-center">
              <Spinner className="text-mediumgrey" />
            </div>
          ) : filteredProjects.length > 0 ? (
            [{ id: "", name: NO_PROJECT_LABEL }, ...filteredProjects].map(
              (project) => {
                const isSelected =
                  String(project?.id || "") ===
                  String(selectedProjectId || "");
                return (
                  <button
                    key={project?.id || "all"}
                    type="button"
                    className={cn(
                      "w-full px-3 py-1.5 text-left text-sm rounded-lg flex items-center justify-between transition-colors cursor-pointer",
                      isSelected
                        ? "bg-primary/5 text-primary font-medium"
                        : "hover:bg-surface text-grey",
                    )}
                    onClick={() => handleProjectSelect(project?.id || "")}
                  >
                    <span className="truncate">{project?.name}</span>
                    {isSelected ? (
                      <CheckIcon
                        width={14}
                        height={14}
                        className="text-primary"
                      />
                    ) : null}
                  </button>
                );
              },
            )
          ) : (
            <div className="py-4 text-center text-xs text-mediumgrey">
              No projects found
            </div>
          )}
        </div>

        {/* Sessions section */}
        <div className="px-3 py-2 border-t border-surface-border bg-surface/50 flex items-center justify-between">
          <span className="text-[10px] font-semibold text-mediumgrey uppercase tracking-wider">
            Sessions
          </span>
          <Button
            className="justify-center gap-1.5 rounded-lg bg-primary hover:bg-primary/90 text-white shadow-sm h-6 text-[11px] px-2.5"
            size="sm"
            onClick={handleCreateSession}
            disabled={creatingSession}
          >
            <PlusIcon width={10} height={10} />
            New Chat
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <SessionList onSelect={handleSessionSelect} />
        </div>
        {activeSession?.id ? (
          <div className="border-t border-surface-border bg-surface/50 px-3 py-2 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-mediumgrey uppercase tracking-wider">
                Summary
              </span>
              <span className="text-[10px] text-grey">
                v{summaryMeta?.version || 0}
              </span>
            </div>
            <div className="flex gap-1.5">
              <Button
                className="flex-1 h-6 rounded-md bg-primary text-[11px] text-white"
                size="sm"
                onClick={() => runSummary(regenerate)}
                disabled={regenerating}
              >
                <FiRefreshCcw size={10} /> Regenerate
              </Button>
              <Button
                className="flex-1 h-6 rounded-md bg-danger/10 text-[11px] text-danger"
                size="sm"
                onClick={() => runSummary(clear)}
                disabled={clearing}
              >
                <FiTrash2 size={10} /> Clear
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );

    if (isMobile) {
      return (
        <>
          {triggerButton}
          <MobileDrawer
            title="Select Project & Session"
            open={isOpen}
            onClose={handleClose}
          >
            <div className="flex flex-col h-[60vh]">
              {drawerContent}
            </div>
          </MobileDrawer>
        </>
      );
    }

    return (
      <CustomPopover
        open={isOpen}
        setOpen={(nextOpen) => {
          setIsOpen(nextOpen);
          if (!nextOpen) setSearch("");
        }}
        trigger={triggerButton}
        placement="bottom-start"
      >
        <div className="flex flex-col w-[320px] max-h-[520px] bg-white rounded-xl shadow-[0_18px_60px_rgba(15,23,42,0.12),0_4px_14px_rgba(15,23,42,0.08)] border border-surface-border overflow-hidden">
          {drawerContent}
        </div>
      </CustomPopover>
    );
  },
);

DraftFlowNavigator.displayName = "DraftFlowNavigator";
